/**
 * Super Admin — Multi-School Intelligence Engine.
 *
 * Pipeline: aggregate → AI analysis (GPT-4o) → strict-JSON insights → UI + Excel.
 * Backend: /api/content-engine/admin/intelligence/* (super_admin gated).
 * Mounted at /super-admin/intelligence and protected by ProtectedRoute(['super_admin']).
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Activity, AlertTriangle, BarChart3, Brain, Clock,
  Download, Loader2, RefreshCw, Sparkles, Target, TrendingUp, Trophy, Users,
} from 'lucide-react';
import apiService from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';

const PRIORITY_COLOR = {
  High:   'bg-rose-100 text-rose-800 border-rose-300',
  Medium: 'bg-amber-100 text-amber-800 border-amber-300',
  Low:    'bg-slate-100 text-slate-700 border-slate-300',
};

function StatCard({ label, value, sub, icon: Icon, testId }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testId}>
      <div className="h-10 w-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center text-indigo-700 dark:text-indigo-300">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
        <div className="text-xl font-bold tabular-nums">{value ?? '—'}</div>
        {sub && <div className="text-[11px] text-muted-foreground truncate">{sub}</div>}
      </div>
    </Card>
  );
}

function SectionTitle({ icon: Icon, children, testId }) {
  return (
    <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-muted-foreground mt-6 mb-2" data-testid={testId}>
      <Icon className="h-4 w-4 text-indigo-600" /> {children}
    </h2>
  );
}

function Pill({ children, color = 'slate' }) {
  const cls = {
    slate:   'bg-slate-100 text-slate-700 border-slate-300',
    indigo:  'bg-indigo-100 text-indigo-800 border-indigo-300',
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    rose:    'bg-rose-100 text-rose-800 border-rose-300',
    amber:   'bg-amber-100 text-amber-800 border-amber-300',
  }[color] || 'bg-slate-100 text-slate-700';
  return <Badge variant="outline" className={`mr-1 mb-1 ${cls}`}>{children}</Badge>;
}

function BulletList({ items, testId, color = 'slate' }) {
  if (!items || items.length === 0) {
    return <div className="text-xs text-muted-foreground italic" data-testid={`${testId}-empty`}>No insights yet.</div>;
  }
  const dot = {
    indigo:  'bg-indigo-500',
    rose:    'bg-rose-500',
    amber:   'bg-amber-500',
    emerald: 'bg-emerald-500',
    slate:   'bg-slate-500',
  }[color] || 'bg-slate-500';
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {items.map((it, i) => (
        <li key={i} className="text-sm flex gap-2">
          <span className={`mt-2 h-1.5 w-1.5 rounded-full shrink-0 ${dot}`} />
          <span className="text-foreground/90">{typeof it === 'string' ? it : JSON.stringify(it)}</span>
        </li>
      ))}
    </ul>
  );
}

export default function IntelligenceEnginePage() {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [record, setRecord] = useState(null);

  const loadLatest = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiService.request('/content-engine/admin/intelligence/latest');
      setRecord(res?.hasReport ? res : null);
    } catch (e) {
      setError(e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  const onRegenerate = async () => {
    setGenerating(true); setError('');
    try {
      const res = await apiService.request('/content-engine/admin/intelligence/regenerate', { method: 'POST' });
      setRecord(res);
    } catch (e) {
      setError(e.message || 'Failed to regenerate');
    } finally {
      setGenerating(false);
    }
  };

  const onExport = async () => {
    if (!record?.reportId) return;
    setDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/content-engine/admin/intelligence/${record.reportId}/export.xlsx`;
      const token = localStorage.getItem('sip_token') || '';
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `intelligence-${record.reportId}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  const r = record?.report || {};
  const gs = r.global_snapshot || {};
  const fi = r.feature_intelligence || {};
  const ssh = r.single_school_highlight || {};
  const bench = r.benchmark_summary || {};
  const cohort = r.cohort_comparison || {};
  const segs = r.user_segments || {};

  return (
    <div className="p-6 space-y-4" data-testid="intelligence-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-indigo-600" />
            Multi-School Intelligence Engine
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cross-school benchmarking · pattern detection · AI-driven product strategy. Privacy-safe (anonymized).
          </p>
          {record?.createdAt && (
            <p className="text-[11px] text-muted-foreground mt-1" data-testid="report-meta">
              Last analyzed: {new Date(record.createdAt).toLocaleString()} · Triggered by: {record.triggeredBy || '—'}
              {r._fallback && <span className="ml-2 text-amber-700 font-medium">(deterministic fallback — LLM unavailable)</span>}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={onRegenerate} disabled={generating}
            className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="regenerate-btn"
          >
            {generating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing…</> : <><RefreshCw className="h-4 w-4 mr-2" />Regenerate Insights</>}
          </Button>
          <Button
            variant="outline" onClick={onExport}
            disabled={!record?.reportId || downloading} data-testid="export-btn"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Export Excel
          </Button>
        </div>
      </div>

      {error && (
        <Card className="p-3 border-rose-300 bg-rose-50 text-rose-800 flex items-center gap-2" data-testid="error-banner">
          <AlertTriangle className="h-4 w-4" /> {error}
        </Card>
      )}

      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : !record ? (
        <Card className="p-12 text-center text-muted-foreground" data-testid="empty-state">
          <Sparkles className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <div className="font-medium text-foreground mb-1">No intelligence report yet</div>
          <div className="text-sm">Click <strong>Regenerate Insights</strong> to run the first analysis.</div>
        </Card>
      ) : (
        <>
          {/* Global Snapshot */}
          <SectionTitle icon={BarChart3} testId="title-global">Global Snapshot</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="global-snapshot">
            <StatCard label="Total Schools"        value={gs.total_schools}         icon={Users}      testId="gs-schools" />
            <StatCard label="Avg Activation Rate"  value={gs.avg_activation_rate}   icon={TrendingUp} testId="gs-activation" />
            <StatCard label="Avg Weekly Retention" value={gs.avg_weekly_retention}  icon={Activity}   testId="gs-retention" />
            <StatCard label="Top Feature"          value={gs.top_feature}           icon={Trophy}     testId="gs-top" />
            <StatCard label="Weakest Feature"      value={gs.weakest_feature}       icon={Target}     testId="gs-weak" />
          </div>

          {/* Alerts */}
          {(r.alerts || []).length > 0 && (
            <>
              <SectionTitle icon={AlertTriangle} testId="title-alerts">Alerts</SectionTitle>
              <Card className="p-4 border-rose-200 bg-rose-50 dark:bg-rose-900/10" data-testid="alerts-card">
                <BulletList items={r.alerts} color="rose" testId="alerts-list" />
              </Card>
            </>
          )}

          {/* Feature Intelligence */}
          <SectionTitle icon={Sparkles} testId="title-features">Feature Intelligence</SectionTitle>
          <Card className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="feature-intelligence">
            <div>
              <div className="text-xs uppercase font-semibold text-muted-foreground mb-2">Top Task Types (Section A)</div>
              <div data-testid="top-task-types">{(fi.top_task_types || []).map((t, i) => <Pill key={i} color="indigo">{t}</Pill>)}</div>
            </div>
            <div>
              <div className="text-xs uppercase font-semibold text-muted-foreground mb-2">Top Alignments (Section B)</div>
              <div data-testid="top-alignments">{(fi.top_alignments || []).map((t, i) => <Pill key={i} color="emerald">{t}</Pill>)}</div>
            </div>
            <div>
              <div className="text-xs uppercase font-semibold text-muted-foreground mb-2">Top Output Types</div>
              <div data-testid="top-outputs">{(fi.top_output_types || []).map((t, i) => <Pill key={i}>{t}</Pill>)}</div>
            </div>
            <div className="md:col-span-3 border-t border-border pt-3">
              <div className="text-sm">🥇 <strong>Best combination:</strong> <span data-testid="best-combo">{fi.best_combination || '—'}</span></div>
              <div className="text-sm mt-1">🧩 <strong>Blueprint impact:</strong> <span data-testid="blueprint-impact">{fi.blueprint_impact || '—'}</span></div>
            </div>
          </Card>

          {/* Single School Highlight */}
          <SectionTitle icon={Trophy} testId="title-single">Single School Highlight</SectionTitle>
          <Card className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="single-school">
            <div className="md:col-span-3 text-sm" data-testid="single-summary">{ssh.summary || '—'}</div>
            <div>
              <div className="text-xs uppercase font-semibold text-emerald-700 mb-1">Strengths</div>
              <BulletList items={ssh.strengths} color="emerald" testId="single-strengths" />
            </div>
            <div>
              <div className="text-xs uppercase font-semibold text-rose-700 mb-1">Weaknesses</div>
              <BulletList items={ssh.weaknesses} color="rose" testId="single-weaknesses" />
            </div>
          </Card>

          {/* Cross-School + Global Patterns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4" data-testid="cross-school">
              <SectionTitle icon={Users} testId="title-cross">Cross-School Insights</SectionTitle>
              <BulletList items={r.cross_school_insights} color="indigo" testId="cross-list" />
            </Card>
            <Card className="p-4" data-testid="global-patterns">
              <SectionTitle icon={Activity} testId="title-patterns">Global Patterns</SectionTitle>
              <BulletList items={r.global_patterns} color="emerald" testId="patterns-list" />
            </Card>
          </div>

          {/* Actions */}
          <SectionTitle icon={Target} testId="title-actions">Recommended Actions</SectionTitle>
          <div className="space-y-2" data-testid="actions-list">
            {(r.actions || []).length === 0 ? (
              <div className="text-xs text-muted-foreground italic">No actions recommended.</div>
            ) : (
              (r.actions || []).map((a, i) => (
                <Card key={i} className="p-3 flex items-start gap-3" data-testid={`action-${i}`}>
                  <Badge variant="outline" className={`mt-0.5 shrink-0 ${PRIORITY_COLOR[a.priority] || PRIORITY_COLOR.Low}`}>{a.priority}</Badge>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">{a.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Scope: <strong>{a.scope}</strong></div>
                    {a.expected_impact && <div className="text-xs mt-1">📈 {a.expected_impact}</div>}
                  </div>
                </Card>
              ))
            )}
          </div>

          {/* Benchmarks + Cohort + Segments */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4" data-testid="benchmark-card">
              <SectionTitle icon={Trophy} testId="title-bench">Benchmark Summary</SectionTitle>
              <div className="space-y-1.5 text-sm">
                <div><strong>Top tier:</strong> <span data-testid="bench-top">{bench.top_tier_behavior || '—'}</span></div>
                <div><strong>Average:</strong> <span data-testid="bench-avg">{bench.average_behavior || '—'}</span></div>
                <div><strong>Underperforming:</strong> <span data-testid="bench-low">{bench.underperforming_behavior || '—'}</span></div>
              </div>
            </Card>
            <Card className="p-4" data-testid="cohort-card">
              <SectionTitle icon={Clock} testId="title-cohort">Cohort Comparison</SectionTitle>
              <div className="space-y-1.5 text-sm">
                <div><strong>Best retention curve:</strong> <span data-testid="cohort-best">{cohort.best_retention_curve || '—'}</span></div>
                <div><strong>Worst retention curve:</strong> <span data-testid="cohort-worst">{cohort.worst_retention_curve || '—'}</span></div>
                <div><strong>Drop-off stage:</strong> <span data-testid="cohort-drop">{cohort.drop_off_stage || '—'}</span></div>
              </div>
            </Card>
            <Card className="p-4" data-testid="segments-card">
              <SectionTitle icon={Users} testId="title-segments">User Segments</SectionTitle>
              <div className="space-y-1.5 text-sm">
                <div><strong>Power users:</strong> <span data-testid="seg-power">{segs.power_users || '—'}</span></div>
                <div><strong>At-risk:</strong> <span data-testid="seg-risk">{segs.at_risk_users || '—'}</span></div>
                <div><strong>Drop-off:</strong> <span data-testid="seg-drop">{segs.drop_off_users || '—'}</span></div>
                {segs.insight && <div className="text-xs italic mt-2">💡 {segs.insight}</div>}
              </div>
            </Card>
          </div>

          {/* Product Opportunities */}
          {(r.product_opportunities || []).length > 0 && (
            <>
              <SectionTitle icon={Sparkles} testId="title-opps">Product Opportunities</SectionTitle>
              <Card className="p-4" data-testid="opportunities-card">
                <BulletList items={r.product_opportunities} color="amber" testId="opportunities-list" />
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
